import {
  COOKIE_CHANGE_EVENT,
  COOKIE_STORAGE_KEY,
  readClientCookie,
} from './clientCookies'

export const COOKIE_SERVER_SNAPSHOT = null

export function createCookieStore(cookieName: string) {
  let snapshot = readClientCookie(cookieName)
  const listeners = new Set<() => void>()

  const checkAndUpdate = () => {
    const newValue = readClientCookie(cookieName)
    if (newValue === snapshot) return

    snapshot = newValue
    listeners.forEach((listener) => listener())
  }

  const handleCustom = (e: CustomEvent<{ name: string }>) => {
    if (e.detail?.name === cookieName) {
      checkAndUpdate()
    }
  }

  const handleStorage = (e: StorageEvent) => {
    if (e.key !== COOKIE_STORAGE_KEY) return

    try {
      if (!e.newValue || JSON.parse(e.newValue).name === cookieName) {
        checkAndUpdate()
      }
    } catch (err) {
      console.error('Error parsing cookie value:', err)
      checkAndUpdate()
    }
  }

  const addListeners = () => {
    window.addEventListener(COOKIE_CHANGE_EVENT, handleCustom)
    window.addEventListener('storage', handleStorage)
    window.addEventListener('focus', checkAndUpdate)
    document.addEventListener('visibilitychange', checkAndUpdate)
  }

  const removeListeners = () => {
    window.removeEventListener(COOKIE_CHANGE_EVENT, handleCustom)
    window.removeEventListener('storage', handleStorage)
    window.removeEventListener('focus', checkAndUpdate)
    document.removeEventListener('visibilitychange', checkAndUpdate)
  }

  function getSnapshot() {
    return snapshot
  }

  function getServerSnapshot() {
    return COOKIE_SERVER_SNAPSHOT
  }

  function subscribe(callback: () => void) {
    const wasEmpty = listeners.size === 0
    listeners.add(callback)
    if (wasEmpty) addListeners()
    // The singleton can be created before a server action updates the cookie.
    // Close that gap as soon as the first consumer subscribes.
    checkAndUpdate()

    return () => {
      listeners.delete(callback)
      if (listeners.size === 0) removeListeners()
    }
  }

  return { subscribe, getSnapshot, getServerSnapshot }
}
